import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "wouter";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  Lock,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  Unlock,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import {
  useSection,
  useUpdateSection,
  useDeleteSection,
  useGenerateSectionDraft,
  useSectionRequirementLinks,
  useReplaceSectionRequirementLinks,
} from "@/hooks/useSections";
import { useMatrixItems } from "@/hooks/useProjects";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useMe, useOrgUsers } from "@/hooks/useMe";
import {
  api,
  type OrgUserSummary,
  type ProjectFile,
  type ProposalSection,
  type ResponseMatrixItem,
  type SectionStatus,
  type TeamProfile,
} from "@/lib/api";
import { useTeamProfiles } from "@/hooks/useTeamProfiles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserSquare2 } from "lucide-react";
import { sectionKey, sectionsKey } from "@/hooks/useSections";

const AUTOSAVE_MS = 1500;

export default function SectionDetail() {
  const params = useParams<{ id: string; sectionId: string }>();
  const projectId = params.id!;
  const sectionId = params.sectionId!;

  const section = useSection(sectionId);
  const update = useUpdateSection(projectId);
  const remove = useDeleteSection(projectId);
  const generate = useGenerateSectionDraft(projectId);

  const [draftText, setDraftText] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [purposeDraft, setPurposeDraft] = useState("");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [pendingDelete, setPendingDelete] = useState(false);
  const lastServerRef = useRef({ draftText: "", title: "", purpose: "" });

  // Hydrate from server.
  useEffect(() => {
    if (!section.data) return;
    const incoming = section.data.draftText ?? "";
    const incomingTitle = section.data.title ?? "";
    const incomingPurpose = section.data.purpose ?? "";
    if (
      incoming !== lastServerRef.current.draftText &&
      draftText === lastServerRef.current.draftText
    ) {
      setDraftText(incoming);
    }
    if (
      incomingTitle !== lastServerRef.current.title &&
      titleDraft === lastServerRef.current.title
    ) {
      setTitleDraft(incomingTitle);
    }
    if (
      incomingPurpose !== lastServerRef.current.purpose &&
      purposeDraft === lastServerRef.current.purpose
    ) {
      setPurposeDraft(incomingPurpose);
    }
    lastServerRef.current = {
      draftText: incoming,
      title: incomingTitle,
      purpose: incomingPurpose,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.data?.draftText, section.data?.title, section.data?.purpose]);

  // Debounced autosave.
  useEffect(() => {
    if (!section.data) return;
    const dirty =
      draftText !== lastServerRef.current.draftText ||
      titleDraft !== lastServerRef.current.title ||
      purposeDraft !== lastServerRef.current.purpose;
    if (!dirty) return;

    const t = setTimeout(() => {
      const patch: Parameters<typeof update.mutateAsync>[0]["patch"] = {};
      if (draftText !== lastServerRef.current.draftText) patch.draftText = draftText;
      if (titleDraft !== lastServerRef.current.title) patch.title = titleDraft;
      if (purposeDraft !== lastServerRef.current.purpose) patch.purpose = purposeDraft;
      update.mutateAsync({ sectionId, patch }).then(() => {
        lastServerRef.current = {
          draftText,
          title: titleDraft,
          purpose: purposeDraft,
        };
        setSavedAt(new Date());
      });
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftText, titleDraft, purposeDraft]);

  const handleGenerate = async () => {
    try {
      const updated = await generate.mutateAsync(sectionId);
      setDraftText(updated.draftText);
      lastServerRef.current = {
        draftText: updated.draftText,
        title: updated.title,
        purpose: updated.purpose ?? "",
      };
      toast.success("Draft generated — review, edit, and tweak before exporting.");
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  if (section.isLoading) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="Section" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  if (section.error || !section.data) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="Section" subtitle="Couldn't load" />
        <div className="p-8">
          <Card>
            <div className="text-sm text-red-400">
              {section.error ? (section.error as Error).message : "Section not found."}
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  const sec = section.data;
  const isProseAi = sec.kind === "prose_ai";
  const dirty =
    draftText !== lastServerRef.current.draftText ||
    titleDraft !== lastServerRef.current.title ||
    purposeDraft !== lastServerRef.current.purpose;

  return (
    <Layout projectId={projectId}>
      <PageHeader
        title={titleDraft || sec.title}
        subtitle={savingLabel(dirty, savedAt)}
        actions={
          <div className="flex items-center gap-2">
            {sec.sensitive && (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border"
                style={{
                  color: "#F59E0B",
                  borderColor: "rgba(245,158,11,0.3)",
                  backgroundColor: "rgba(245,158,11,0.06)",
                }}
                title="Sensitive — restricted to admin/finance and approved viewers"
              >
                <Lock size={11} />
                Sensitive
              </span>
            )}
            <Link href={`/projects/${projectId}/drafts`}>
              <button className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/5 border border-white/10">
                <ArrowLeft size={12} />
                All sections
              </button>
            </Link>
            <button
              onClick={() => setPendingDelete(true)}
              className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 px-2.5 py-1.5 rounded-md hover:bg-red-500/10 border border-red-500/20"
            >
              <Trash2 size={12} />
              Delete section
            </button>
          </div>
        }
      />

      <div className="p-8 pb-24 space-y-4 max-w-3xl">
        {/* Title — common across all kinds */}
        <div>
          <p
            className="text-[10px] uppercase tracking-wider font-bold mb-1.5"
            style={{ color: "#9CA3AF" }}
          >
            Section title
          </p>
          <input
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-orange-400/30"
          />
        </div>

        {/* Purpose — common across all kinds */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p
              className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: "#9CA3AF" }}
            >
              Purpose
            </p>
            <span className="text-[10px]" style={{ color: "#6B7280" }}>
              {isProseAi ? "Fed into AI when drafting" : "What this section is for"}
            </span>
          </div>
          <textarea
            value={purposeDraft}
            onChange={(e) => setPurposeDraft(e.target.value)}
            placeholder="One or two sentences describing what this section should cover."
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30 resize-y"
          />
        </div>

        {/* Requirements addressed — the bridge from Tracker to Response */}
        <RequirementLinksCard projectId={projectId} sectionId={sectionId} />

        {/* Body — switches by kind */}
        {isProseAi && (
          <>
            <Card>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
                  >
                    <Sparkles size={16} style={{ color: "#F97316" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white">Draft this section with AI</h3>
                    <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                      Reads the RFP, About Us, About Client, Strategy, and your reference library of
                      past RFPs (especially wins), then writes a first draft you can edit.
                    </p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Sparkles size={14} />}
                  onClick={handleGenerate}
                  loading={generate.isPending}
                >
                  {sec.draftText ? "Regenerate" : "Generate draft"}
                </Button>
              </div>
            </Card>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p
                  className="text-[10px] uppercase tracking-wider font-bold"
                  style={{ color: "#9CA3AF" }}
                >
                  Draft (markdown)
                </p>
                <span className="text-[10px]" style={{ color: dirty ? "#9CA3AF" : "#22c55e" }}>
                  {dirty ? "Saving…" : savedAt ? "Saved" : ""}
                </span>
              </div>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder="Write or generate the draft. Markdown formatting is preserved on export to DOCX."
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30 resize-y font-mono"
                style={{ minHeight: 320 }}
              />
              <p className="text-[11px] mt-1.5" style={{ color: "#6B7280" }}>
                Tip: use **bold**, *italic*, and bullets (`-`). Headings (`#`) are added by the
                export step — write paragraphs here.
              </p>
            </div>
          </>
        )}

        {isFileBacked(sec.kind) && (
          <FileBackedEditor
            section={sec}
            projectId={projectId}
            onSetAsset={async (fileId) => {
              await update.mutateAsync({
                sectionId,
                patch: {
                  assetFileId: fileId,
                  status: fileId ? "draft_ready" : "not_started",
                },
              });
            }}
          />
        )}

        {sec.kind === "personnel" && (
          <PersonnelEditor
            section={sec}
            projectId={projectId}
            onChangeFeatured={async (ids) => {
              await update.mutateAsync({
                sectionId,
                patch: { featuredProfileIds: ids },
              });
            }}
          />
        )}

        {isSystemDriven(sec.kind) && (
          <Card>
            <div className="text-sm" style={{ color: "#9CA3AF" }}>
              <p className="text-white text-sm font-medium mb-2">{kindLabel(sec.kind)} section</p>
              <p className="text-xs">
                {sec.kind === "compliance"
                  ? "Compliance acknowledgements live in the Requirements Tracker. The DOCX export references them automatically; you don't need to write anything here."
                  : "The detail editor for this kind isn't built yet. Title, purpose, owner, and status sync via the RFP Response list."}
              </p>
            </div>
          </Card>
        )}

        {/* Status — common across all kinds */}
        <Card>
          <div className="flex items-center gap-3">
            <p
              className="text-[10px] uppercase tracking-wider font-bold w-20 flex-shrink-0"
              style={{ color: "#9CA3AF" }}
            >
              Status
            </p>
            <select
              value={sec.status}
              onChange={(e) =>
                update.mutate({
                  sectionId,
                  patch: { status: e.target.value as SectionStatus },
                })
              }
              className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none"
            >
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="draft_ready">Draft ready</option>
              <option value="in_review">In review</option>
              <option value="complete">Final</option>
            </select>
          </div>
        </Card>

        {/* Access management — admin-only. Shows the sensitive flag + the
            per-section viewer allowlist. Members never render this card. */}
        <SectionAccessCard
          section={sec}
          onTogglesensitive={async (next) => {
            await update.mutateAsync({
              sectionId,
              patch: { sensitive: next },
            });
          }}
          onChangeAllowlist={async (next) => {
            await update.mutateAsync({
              sectionId,
              patch: { additionalViewerIds: next },
            });
          }}
        />
      </div>

      <ConfirmDeleteDialog
        open={pendingDelete}
        onOpenChange={(o) => setPendingDelete(o)}
        title="Delete this section?"
        description={`"${sec.title}" will be removed from the response. The draft text will be lost.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          try {
            await remove.mutateAsync(sectionId);
            toast.success("Section deleted.");
            window.history.back();
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
        busy={remove.isPending}
      />
    </Layout>
  );
}

function savingLabel(dirty: boolean, savedAt: Date | null): string {
  if (dirty) return "Saving…";
  if (savedAt)
    return `Saved ${Math.max(0, Math.round((Date.now() - savedAt.getTime()) / 1000))}s ago`;
  return "";
}

function kindLabel(kind: ProposalSection["kind"]): string {
  switch (kind) {
    case "prose_external":
      return "External prose";
    case "designer_asset":
      return "Designer asset";
    case "template":
      return "Templated";
    case "personnel":
      return "CV / Personnel";
    case "pricing":
      return "Pricing";
    case "attachment":
      return "Attachment";
    case "compliance":
      return "Compliance";
    default:
      return "Prose";
  }
}

/** Section kinds that take a single file as their content. */
function isFileBacked(kind: ProposalSection["kind"]): boolean {
  return (
    kind === "prose_external" ||
    kind === "designer_asset" ||
    kind === "attachment" ||
    kind === "pricing"
  );
}

/** Section kinds whose content lives elsewhere in the system. */
function isSystemDriven(kind: ProposalSection["kind"]): boolean {
  return kind === "template" || kind === "compliance";
}

/**
 * Per-kind copy for the file-upload editor. Tells the user what to upload
 * and where the file fits in the final response.
 */
const FILE_KIND_COPY: Record<
  "prose_external" | "designer_asset" | "attachment" | "pricing",
  { title: string; intent: string; accept: string }
> = {
  prose_external: {
    title: "Upload the written content",
    intent:
      "A Word doc or PDF the team wrote elsewhere. The text gets pulled into the response on export.",
    accept:
      ".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf",
  },
  designer_asset: {
    title: "Upload the designer asset",
    intent: "A PDF or PNG export from Figma / Adobe. Embedded inline in the response on export.",
    accept: ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg",
  },
  attachment: {
    title: "Upload the required document",
    intent:
      "Insurance certificate, ISO cert, audited financials, or other pre-existing file. Bundled with the submission package on export.",
    accept:
      ".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  pricing: {
    title: "Upload the pricing schedule",
    intent:
      "A spreadsheet or PDF with the priced offer. Treated as sensitive once permissions ship.",
    accept:
      ".xlsx,.pdf,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
};

/**
 * Universal file-upload editor for prose_external, designer_asset, attachment,
 * and pricing kinds. Drops a file → presigned PUT to R2 → finalize →
 * section.assetFileId set. Image kinds get a thumbnail; everything else gets
 * a file icon and metadata. Replace and remove are first-class.
 */
function FileBackedEditor({
  section,
  projectId,
  onSetAsset,
}: {
  section: ProposalSection;
  projectId: string;
  onSetAsset: (fileId: string | null) => Promise<void>;
}) {
  const kind = section.kind as keyof typeof FILE_KIND_COPY;
  const copy = FILE_KIND_COPY[kind];

  const upload = useFileUpload({ kind: "attachment", projectId });
  const [asset, setAsset] = useState<ProjectFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch the asset's metadata when the section's assetFileId changes.
  useEffect(() => {
    if (!section.assetFileId) {
      setAsset(null);
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    api
      .getFile(section.assetFileId)
      .then((f: ProjectFile) => {
        if (!cancelled) setAsset(f);
      })
      .catch(() => {
        if (!cancelled) setAsset(null);
      });
    return () => {
      cancelled = true;
    };
  }, [section.assetFileId]);

  // For image assets, get a presigned URL for the inline preview.
  useEffect(() => {
    if (!asset || !asset.contentType.startsWith("image/")) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    api
      .getFileDownloadUrl(asset.id)
      .then((r: { downloadUrl: string }) => {
        if (!cancelled) setPreviewUrl(r.downloadUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [asset?.id, asset?.contentType]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file) return;
    try {
      const finalized = await upload.upload(file);
      await onSetAsset(finalized.id);
      toast.success("File uploaded.");
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleViewOriginal = async () => {
    if (!asset) return;
    try {
      const r = await api.getFileDownloadUrl(asset.id);
      window.open(r.downloadUrl, "_blank", "noopener");
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleRemove = async () => {
    try {
      await onSetAsset(null);
      toast.success("File removed from this section.");
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  // Drag-and-drop wiring.
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // ---------- Render: uploaded asset ----------
  if (asset) {
    const sizeKb = Math.max(1, Math.round(asset.byteSize / 1024));
    const sizeLabel = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`;
    return (
      <Card>
        <div className="flex items-start gap-4">
          {/* Preview / icon */}
          {previewUrl ? (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block flex-shrink-0 rounded-lg overflow-hidden border"
              style={{
                borderColor: "rgba(229,231,235,0.08)",
                backgroundColor: "rgba(255,255,255,0.02)",
              }}
            >
              <img
                src={previewUrl}
                alt={asset.filename}
                style={{ width: 96, height: 96, objectFit: "cover" }}
              />
            </a>
          ) : (
            <div
              className="w-24 h-24 rounded-lg flex items-center justify-center flex-shrink-0 border"
              style={{
                borderColor: "rgba(229,231,235,0.08)",
                backgroundColor: "rgba(255,255,255,0.02)",
              }}
            >
              {asset.contentType.startsWith("image/") ? (
                <ImageIcon size={28} className="text-gray-300" />
              ) : (
                <FileText size={28} className="text-gray-300" />
              )}
            </div>
          )}

          {/* Metadata + actions */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{asset.filename}</p>
            <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
              {prettyContentType(asset.contentType)} · {sizeLabel}
            </p>
            <p className="text-xs mt-2" style={{ color: "#9CA3AF" }}>
              {copy.intent}
            </p>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                icon={<ExternalLink size={12} />}
                onClick={handleViewOriginal}
              >
                View original
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<Upload size={12} />}
                onClick={() => inputRef.current?.click()}
                loading={
                  upload.progress.status !== "idle" &&
                  upload.progress.status !== "done" &&
                  upload.progress.status !== "error"
                }
              >
                Replace
              </Button>
              <button
                onClick={handleRemove}
                className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 px-2.5 py-1.5 rounded-md hover:bg-red-500/10"
              >
                <X size={12} />
                Remove
              </button>
            </div>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={copy.accept}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </Card>
    );
  }

  // ---------- Render: empty drop zone ----------
  const inProgress =
    upload.progress.status !== "idle" &&
    upload.progress.status !== "done" &&
    upload.progress.status !== "error";

  return (
    <Card>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => !inProgress && inputRef.current?.click()}
        className="rounded-lg border-2 border-dashed px-5 py-10 text-center cursor-pointer transition-colors"
        style={{
          borderColor: isDragging ? "rgba(249,115,22,0.5)" : "rgba(229,231,235,0.15)",
          backgroundColor: isDragging ? "rgba(249,115,22,0.05)" : "rgba(255,255,255,0.01)",
        }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-3"
          style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
        >
          <Upload size={18} style={{ color: "#F97316" }} />
        </div>
        <p className="text-sm font-semibold text-white">{copy.title}</p>
        <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
          {copy.intent}
        </p>
        <p className="text-[11px] mt-3" style={{ color: "#6B7280" }}>
          {inProgress
            ? `${upload.progress.status === "uploading" ? "Uploading" : upload.progress.status === "finalizing" ? "Finalizing" : "Preparing"}… ${upload.progress.pct}%`
            : "Drag a file here, or click to choose. Max 10 MB."}
        </p>
        {upload.progress.status === "error" && upload.progress.error && (
          <p className="text-[11px] mt-2 text-red-400">{upload.progress.error}</p>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={copy.accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </Card>
  );
}

/**
 * "Requirements addressed by this section" card — the bridge from the
 * Requirements Tracker to the response. Shows currently-linked requirements
 * as removable chips, with a "+ Link a requirement" picker that opens a
 * searchable list of the project's matrix items.
 *
 * The replace-set semantics are intentional: every change is committed
 * as a full PUT so the working state and the persisted state never drift.
 */
function RequirementLinksCard({ projectId, sectionId }: { projectId: string; sectionId: string }) {
  const links = useSectionRequirementLinks(sectionId);
  const matrix = useMatrixItems(projectId);
  const replaceLinks = useReplaceSectionRequirementLinks(projectId, sectionId);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const linkedItems = links.data?.links?.map((l) => l.matrixItem) ?? [];
  const linkedIds = new Set(linkedItems.map((i) => i.id));
  const allMatrix: ResponseMatrixItem[] = matrix.data ?? [];

  const q = search.trim().toLowerCase();
  const candidates = allMatrix
    .filter((m) => !linkedIds.has(m.id))
    .filter((m) => {
      if (!q) return true;
      return m.requirement.toLowerCase().includes(q) || m.section.toLowerCase().includes(q);
    })
    .slice(0, 50);

  const addLink = async (matrixItemId: string) => {
    const next = [...linkedIds, matrixItemId];
    try {
      await replaceLinks.mutateAsync(next);
      setSearch("");
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const removeLink = async (matrixItemId: string) => {
    const next = [...linkedIds].filter((id) => id !== matrixItemId);
    try {
      await replaceLinks.mutateAsync(next);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: "rgba(99,102,241,0.1)" }}
        >
          <Link2 size={16} style={{ color: "#6366F1" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-white">Requirements addressed</h3>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                Link this section to the RFP requirements it covers. The tracker shows which
                sections handle each requirement, and the response page flags any that have nowhere
                to live.
              </p>
            </div>
          </div>

          {/* Linked chips */}
          <div className="mt-3 flex items-start gap-2 flex-wrap">
            {links.isLoading ? (
              <span className="text-xs" style={{ color: "#9CA3AF" }}>
                <Loader2 size={12} className="inline animate-spin mr-1" />
                Loading…
              </span>
            ) : linkedItems.length === 0 ? (
              <span className="text-xs italic" style={{ color: "#6B7280" }}>
                No requirements linked yet.
              </span>
            ) : (
              linkedItems.map((m) => {
                const isSynced = Boolean(m.externalTaskId ?? m.teamworkTaskId);
                return (
                  <span
                    key={m.id}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border max-w-full"
                    style={{
                      backgroundColor: "rgba(99,102,241,0.08)",
                      borderColor: "rgba(99,102,241,0.2)",
                      color: "#C7D2FE",
                    }}
                    title={
                      isSynced
                        ? `${m.section} · ${m.requirement} · tracked in your PM tool`
                        : `${m.section} · ${m.requirement}`
                    }
                  >
                    {m.clauseRef && (
                      <span className="font-mono" style={{ color: "#9CA3AF" }}>
                        {m.clauseRef}
                      </span>
                    )}
                    <span className="truncate" style={{ maxWidth: 260 }}>
                      {m.requirement}
                    </span>
                    {isSynced && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold"
                        style={{ color: "#22c55e" }}
                        title="This requirement is tracked as a task in your PM tool"
                      >
                        <CheckCircle2 size={9} />
                        Sync
                      </span>
                    )}
                    <button
                      onClick={() => removeLink(m.id)}
                      disabled={replaceLinks.isPending}
                      className="text-gray-400 hover:text-red-300 -mr-1"
                      title="Unlink"
                    >
                      <X size={11} />
                    </button>
                  </span>
                );
              })
            )}

            {/* Add picker */}
            <div ref={pickerRef} className="relative">
              <button
                onClick={() => setPickerOpen((v) => !v)}
                disabled={replaceLinks.isPending}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-dashed text-gray-300 hover:bg-white/5"
                style={{ borderColor: "rgba(229,231,235,0.2)" }}
              >
                <Plus size={11} />
                Link a requirement
              </button>

              {pickerOpen && (
                <div
                  className="absolute left-0 top-full mt-1 w-96 rounded-lg border shadow-xl py-2 z-20"
                  style={{
                    backgroundColor: "#1F2933",
                    borderColor: "rgba(229,231,235,0.12)",
                  }}
                >
                  <div className="px-2 pb-2 relative">
                    <Search
                      size={12}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search requirements…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-7 pr-2 py-1.5 text-xs text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30"
                    />
                  </div>

                  <div className="max-h-72 overflow-y-auto">
                    {matrix.isLoading ? (
                      <p className="px-3 py-3 text-xs text-gray-400 text-center">
                        <Loader2 size={12} className="inline animate-spin mr-1" />
                        Loading requirements…
                      </p>
                    ) : candidates.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-gray-500 text-center">
                        {q
                          ? "No requirements match."
                          : allMatrix.length === 0
                            ? "No requirements parsed yet."
                            : "All requirements are already linked."}
                      </p>
                    ) : (
                      candidates.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => addLink(m.id)}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-start gap-2"
                        >
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-[10px] uppercase tracking-wider font-semibold"
                              style={{ color: "#9CA3AF" }}
                            >
                              {m.section}
                              {m.clauseRef && (
                                <span className="font-mono ml-1.5" style={{ color: "#6B7280" }}>
                                  {m.clauseRef}
                                </span>
                              )}
                            </p>
                            <p className="text-white mt-0.5 truncate">{m.requirement}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function prettyContentType(ct: string): string {
  if (ct === "application/pdf") return "PDF";
  if (ct === "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
    return "Word document";
  if (ct === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
    return "Excel spreadsheet";
  if (ct.startsWith("image/")) return "Image";
  return ct.split("/").pop() ?? ct;
}

// ---------------------------------------------------------------------------
// SectionAccessCard
//
// Admin-only control surface for sensitive sections. Renders nothing for
// non-admins (the server is the source of truth on enforcement; this card
// is purely an authoring affordance).
//
// Three controls:
//   - Sensitive toggle. Default-on for kind=pricing. When on, members lose
//     access unless they're the owner/creator or in the allowlist.
//   - Allowlist editor. Multi-select of org users. Added users get view
//     and edit access.
//   - Note explaining who already has access regardless of the allowlist
//     (admin/finance and the section's owner/creator).
// ---------------------------------------------------------------------------

function SectionAccessCard({
  section,
  onTogglesensitive,
  onChangeAllowlist,
}: {
  section: ProposalSection;
  onTogglesensitive: (next: boolean) => Promise<void>;
  onChangeAllowlist: (next: string[]) => Promise<void>;
}) {
  const me = useMe();
  const orgUsers = useOrgUsers();
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Hide the card entirely from non-admins. Backend already enforces
  // — this is just to avoid showing a control that would 4xx on click.
  if (me.data?.user.role !== "admin") return null;

  const allow = section.additionalViewerIds ?? [];
  const orgUserList: OrgUserSummary[] = orgUsers.data?.users ?? [];
  const allowedUsers = orgUserList.filter((u) => allow.includes(u.id));
  const otherUsers = orgUserList.filter((u) => !allow.includes(u.id));

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{
            backgroundColor: section.sensitive ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.04)",
          }}
        >
          {section.sensitive ? (
            <Lock size={16} style={{ color: "#F59E0B" }} />
          ) : (
            <Unlock size={16} style={{ color: "#9CA3AF" }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-white">Section access</h3>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                {section.sensitive
                  ? "Sensitive — only admin, finance, the owner/creator, and people in the allowlist can see this section."
                  : "Default — every member of the org can see this section."}
              </p>
            </div>
            <button
              onClick={async () => {
                setBusy(true);
                try {
                  await onTogglesensitive(!section.sensitive);
                  toast.success(!section.sensitive ? "Marked sensitive." : "Marked default.");
                } catch (err) {
                  toast.error((err as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="text-xs px-2.5 py-1.5 rounded-md border border-white/10 text-white hover:bg-white/5 disabled:opacity-50"
            >
              {section.sensitive ? "Make default" : "Mark sensitive"}
            </button>
          </div>

          {section.sensitive && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 text-[11px]" style={{ color: "#9CA3AF" }}>
                <ShieldCheck size={12} />
                <span>Always allowed: admins, finance, and the section's owner/creator.</span>
              </div>
              <div>
                <p
                  className="text-[10px] uppercase tracking-wider font-bold mb-1.5"
                  style={{ color: "#9CA3AF" }}
                >
                  Additional viewers ({allowedUsers.length})
                </p>
                {allowedUsers.length === 0 ? (
                  <p className="text-xs" style={{ color: "#6B7280" }}>
                    Nobody added yet.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {allowedUsers.map((u) => (
                      <span
                        key={u.id}
                        className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-white/5 border border-white/10"
                      >
                        <span className="text-white">{u.displayName || u.email}</span>
                        <button
                          onClick={async () => {
                            setBusy(true);
                            try {
                              await onChangeAllowlist(allow.filter((id) => id !== u.id));
                            } catch (err) {
                              toast.error((err as Error).message);
                            } finally {
                              setBusy(false);
                            }
                          }}
                          className="text-gray-400 hover:text-white"
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-2">
                  <button
                    onClick={() => setPickerOpen((v) => !v)}
                    className="text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/10 text-gray-300 hover:text-white hover:bg-white/5"
                  >
                    <Plus size={11} />
                    Add viewer
                  </button>
                  {pickerOpen && (
                    <div
                      className="mt-2 max-h-48 overflow-y-auto rounded-lg border bg-black/40"
                      style={{ borderColor: "rgba(229,231,235,0.08)" }}
                    >
                      {otherUsers.length === 0 ? (
                        <div className="px-3 py-2 text-xs" style={{ color: "#6B7280" }}>
                          No more users to add.
                        </div>
                      ) : (
                        otherUsers.map((u) => (
                          <button
                            key={u.id}
                            onClick={async () => {
                              setBusy(true);
                              try {
                                await onChangeAllowlist([...allow, u.id]);
                                setPickerOpen(false);
                              } catch (err) {
                                toast.error((err as Error).message);
                              } finally {
                                setBusy(false);
                              }
                            }}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-white/5"
                          >
                            <span className="text-white">{u.displayName || u.email}</span>{" "}
                            <span style={{ color: "#6B7280" }}>· {u.role}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PersonnelEditor — kind=personnel section editor
//
// Three pieces:
//   1. The featured-profile picker. Shows the org's roster from the
//      Settings → Team Profiles store and lets the bid manager pick which
//      people appear in this CV section.
//   2. The "Generate tailored CV section" CTA. Server runs the tailor
//      prompt once per featured profile, stitches the markdown, persists
//      it as the section's draftText.
//   3. A read-only preview of the generated draft (markdown). Editing
//      happens in the same prose textarea pattern as prose_ai sections —
//      we just defer to update.mutateAsync to save changes.
// ---------------------------------------------------------------------------

function PersonnelEditor({
  section,
  projectId,
  onChangeFeatured,
}: {
  section: ProposalSection;
  projectId: string;
  onChangeFeatured: (ids: string[]) => Promise<void>;
}) {
  const profilesQ = useTeamProfiles();
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftText, setDraftText] = useState(section.draftText);
  const [savingDraft, setSavingDraft] = useState(false);

  // Hydrate when the server-side section changes (e.g. after tailor).
  useEffect(() => {
    setDraftText(section.draftText);
  }, [section.id, section.draftText]);

  const tailor = useMutation({
    mutationFn: () => api.tailorPersonnelSection(section.id),
    onSuccess(updated) {
      qc.setQueryData(sectionKey(section.id), updated);
      qc.invalidateQueries({ queryKey: sectionsKey(projectId) });
      toast.success("Tailored CV section generated.");
    },
    onError(err) {
      toast.error((err as Error).message);
    },
  });

  const saveDraft = useMutation({
    mutationFn: (text: string) => api.updateSection(section.id, { draftText: text }),
    onSuccess(updated) {
      qc.setQueryData(sectionKey(section.id), updated);
      qc.invalidateQueries({ queryKey: sectionsKey(projectId) });
    },
  });

  const featuredIds = section.featuredProfileIds ?? [];
  const allProfiles: TeamProfile[] = profilesQ.data?.profiles ?? [];
  const featuredProfiles = featuredIds
    .map((id) => allProfiles.find((p) => p.id === id))
    .filter((p): p is TeamProfile => Boolean(p));
  const otherProfiles = allProfiles.filter((p) => !featuredIds.includes(p.id));

  const onAdd = async (id: string) => {
    await onChangeFeatured([...featuredIds, id]);
    setPickerOpen(false);
  };
  const onRemove = async (id: string) => {
    await onChangeFeatured(featuredIds.filter((x) => x !== id));
  };
  const onMove = async (id: string, dir: -1 | 1) => {
    const idx = featuredIds.indexOf(id);
    if (idx < 0) return;
    const next = [...featuredIds];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap]!, next[idx]!];
    await onChangeFeatured(next);
  };

  return (
    <>
      <Card>
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <UserSquare2 size={14} style={{ color: "#9CA3AF" }} />
                Featured team
              </h3>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                Pick people from your org's roster. The AI tailors each canonical CV against this
                RFP and stitches the result into the draft below.{" "}
                <Link href="/settings/team-profiles">
                  <span className="text-orange-400 hover:underline cursor-pointer">
                    Manage roster →
                  </span>
                </Link>
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={<Sparkles size={14} />}
              onClick={() => tailor.mutate()}
              loading={tailor.isPending}
              disabled={featuredIds.length === 0}
              title={
                featuredIds.length === 0
                  ? "Pick at least one profile first"
                  : "Generate tailored CV section"
              }
            >
              {section.draftText ? "Regenerate" : "Generate"}
            </Button>
          </div>

          {profilesQ.isLoading ? (
            <div className="py-4 flex items-center justify-center">
              <Loader2 size={16} className="animate-spin text-orange-400" />
            </div>
          ) : allProfiles.length === 0 ? (
            <div
              className="rounded-lg border px-3 py-4 text-xs text-center"
              style={{
                color: "#9CA3AF",
                borderColor: "rgba(229,231,235,0.08)",
                backgroundColor: "rgba(255,255,255,0.02)",
              }}
            >
              No team profiles yet.{" "}
              <Link href="/settings/team-profiles">
                <span className="text-orange-400 hover:underline cursor-pointer">Add some →</span>
              </Link>
            </div>
          ) : (
            <div className="space-y-1.5">
              {featuredProfiles.map((p, idx) => (
                <div
                  key={p.id}
                  className="rounded-lg border px-3 py-2 flex items-center gap-3"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.02)",
                    borderColor: "rgba(229,231,235,0.08)",
                  }}
                >
                  <span
                    className="text-[10px] font-mono w-5 text-right flex-shrink-0"
                    style={{ color: "#6B7280" }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {p.fullName}
                      {p.role && (
                        <span style={{ color: "#9CA3AF" }} className="ml-1.5">
                          · {p.role}
                        </span>
                      )}
                    </p>
                    {p.expertiseTags.length > 0 && (
                      <p className="text-[10px] mt-0.5 truncate" style={{ color: "#6B7280" }}>
                        {p.expertiseTags.slice(0, 4).join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => onMove(p.id, -1)}
                      disabled={idx === 0}
                      className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/5 disabled:opacity-30"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => onMove(p.id, 1)}
                      disabled={idx === featuredProfiles.length - 1}
                      className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/5 disabled:opacity-30"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => onRemove(p.id)}
                      className="text-gray-400 hover:text-red-400 p-1 rounded hover:bg-red-500/10"
                      title="Remove from this section"
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ))}

              <div className="relative">
                <button
                  onClick={() => setPickerOpen((v) => !v)}
                  className="w-full text-xs inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed text-gray-300 hover:text-white hover:bg-white/5"
                  style={{ borderColor: "rgba(229,231,235,0.15)" }}
                >
                  <Plus size={12} />
                  Add team member
                </button>
                {pickerOpen && (
                  <div
                    className="absolute left-0 right-0 mt-1.5 z-10 rounded-lg border bg-black/90 backdrop-blur max-h-72 overflow-y-auto shadow-xl"
                    style={{ borderColor: "rgba(229,231,235,0.08)" }}
                  >
                    {otherProfiles.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-center" style={{ color: "#6B7280" }}>
                        Everyone in the roster is already featured.
                      </div>
                    ) : (
                      otherProfiles.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => onAdd(p.id)}
                          className="w-full text-left px-3 py-2 hover:bg-white/5"
                        >
                          <p className="text-sm text-white">{p.fullName}</p>
                          <p className="text-[10px]" style={{ color: "#9CA3AF" }}>
                            {p.role || "No role set"}
                            {p.yearsExperience ? ` · ${p.yearsExperience}y` : ""}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Draft editor — same pattern as prose_ai. The Generate button
          replaces; manual edits patch. */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p
            className="text-[10px] uppercase tracking-wider font-bold"
            style={{ color: "#9CA3AF" }}
          >
            Tailored CV draft (markdown)
          </p>
          <span className="text-[10px]" style={{ color: "#6B7280" }}>
            {savingDraft ? "Saving…" : ""}
          </span>
        </div>
        <textarea
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          onBlur={async () => {
            if (draftText === section.draftText) return;
            setSavingDraft(true);
            try {
              await saveDraft.mutateAsync(draftText);
            } finally {
              setSavingDraft(false);
            }
          }}
          placeholder="Click Generate to produce the tailored CV section. You can also write/edit by hand here — markdown formatting is preserved on export."
          className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30 resize-y font-mono"
          style={{ minHeight: 280 }}
        />
        <p className="text-[11px] mt-1.5" style={{ color: "#6B7280" }}>
          Tip: Generate writes a fresh draft from your featured roster. Manual edits save on blur.
        </p>
      </div>
    </>
  );
}
